import type {
  ApiChatMessage,
  ApiChatSource,
  ApiDocument,
  ApiLibrary,
  ApiSendMessageResponse,
  ApiThread,
  ApiWorkspace,
} from "./types.js";
import type {
  DocumentStatus,
  RagChat,
  RagCitation,
  RagCitationAttribute,
  RagDocument,
  RagLibrary,
  RagMember,
  RagMessage,
  RagWorkspace,
} from "../types.js";
import type { AttachedDoc } from "../components/ChatComposer.js";
import { parsePageNumber } from "../lib/document-open.js";

const STATUS_MAP: Record<string, DocumentStatus> = {
  PENDING: "pending",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
};

export function mapApiProcessingStatus(status: string): DocumentStatus {
  return STATUS_MAP[status] ?? "pending";
}

export function mapThreadDocumentsToAttachedDocs(thread: ApiThread): AttachedDoc[] {
  return (thread.documents ?? []).map((entry) => ({
    id: entry.document.id,
    name: entry.document.title,
    status: mapApiProcessingStatus(entry.document.processingStatus),
  }));
}

const MIME_TYPE_LABELS: Record<string, RagDocument["type"]> = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
  "text/html": "HTML",
  "text/markdown": "MD",
  "text/csv": "CSV",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
};

const KNOWN_EXTENSIONS = ["PDF", "DOCX", "PPTX", "HTML", "MD", "CSV", "XLSX"] as const;

/** Strip folder prefixes from upload titles (e.g. folder picker relative paths). */
export function documentDisplayName(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return trimmed;
  const parts = trimmed.split(/[/\\]/);
  return parts[parts.length - 1] || trimmed;
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

/** Auto-provisioned default workspaces use "{username}'s Default Workspace" — show "Default Workspace" instead. */
export function formatWorkspaceDisplayName(name: string): string {
  if (/'s Default Workspace$/i.test(name.trim())) {
    return "Default Workspace";
  }
  return name;
}

export function isDefaultWorkspace(
  workspace: Pick<ApiWorkspace, "name" | "isDefault" | "kind">,
): boolean {
  if (workspace.kind === "ORGANIZATION") {
    return true;
  }

  if (workspace.isDefault) {
    return true;
  }

  const name = workspace.name.trim();
  return name === "Default" || /'s Default Workspace$/i.test(name);
}

function inferDocumentType(
  mimeType: string | null,
  title: string
): RagDocument["type"] {
  if (mimeType && MIME_TYPE_LABELS[mimeType]) {
    return MIME_TYPE_LABELS[mimeType];
  }
  const basename = documentDisplayName(title);
  const dot = basename.lastIndexOf(".");
  if (dot > 0) {
    const ext = basename.slice(dot + 1).toUpperCase();
    if ((KNOWN_EXTENSIONS as readonly string[]).includes(ext)) {
      return ext as RagDocument["type"];
    }
  }
  return "PDF";
}

export function mapWorkspaceUserRole(role: string): RagMember["role"] {
  switch (role.toUpperCase()) {
    case "ADMIN":
      return "admin";
    case "VIEWER":
      return "viewer";
    default:
      return "member";
  }
}

/** RAG usernames append _{first 8 chars of userId} — strip before showing in UI. */
const AUTO_USERNAME_SUFFIX = /_[a-zA-Z0-9]{8}$/;

function titleCaseWords(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatLocalPartForDisplay(localPart: string): string {
  const normalized = localPart.replace(/[._]+/g, " ").trim();
  return titleCaseWords(normalized);
}

export function formatRagMemberDisplayName(
  username: string | null | undefined,
  email: string,
): string {
  let raw = username?.trim() ?? "";
  if (raw && AUTO_USERNAME_SUFFIX.test(raw)) {
    raw = raw.replace(AUTO_USERNAME_SUFFIX, "");
  }

  if (raw) {
    return formatLocalPartForDisplay(raw);
  }

  const localPart = email.split("@")[0]?.trim();
  if (localPart) {
    return formatLocalPartForDisplay(localPart);
  }

  return email;
}

export function mapWorkspaceUserToMember(
  user: { id: string; username: string | null; email: string },
  role: string,
  ownerId: string,
): RagMember | null {
  if (user.id === ownerId) {
    return null;
  }

  return {
    id: user.id,
    name: formatRagMemberDisplayName(user.username, user.email),
    email: user.email,
    role: mapWorkspaceUserRole(role),
  };
}

function computeWorkspaceMemberCount(
  workspace: ApiWorkspace,
  members: RagMember[],
): number {
  if (workspace.users !== undefined) {
    return members.length;
  }

  const workspaceUserRows = workspace._count?.users ?? 0;

  if (workspace.kind === "ORGANIZATION") {
    return workspaceUserRows + (workspace.owner ? 1 : 0);
  }

  return workspaceUserRows || members.length;
}

export function mapWorkspace(workspace: ApiWorkspace): RagWorkspace {
  const members: RagMember[] = [];

  if (workspace.owner) {
    members.push({
      id: workspace.owner.id,
      name: formatRagMemberDisplayName(workspace.owner.username, workspace.owner.email),
      email: workspace.owner.email,
      role: "owner",
    });
  }

  for (const entry of workspace.users ?? []) {
    if (entry.user.id === workspace.ownerId) continue;
    members.push({
      id: entry.user.id,
      name: formatRagMemberDisplayName(entry.user.username, entry.user.email),
      email: entry.user.email,
      role: mapWorkspaceUserRole(entry.role),
    });
  }

  const libraryCount = workspace._count?.libraries ?? workspace.libraries?.length ?? 0;
  const documentCount =
    workspace._count?.documents ??
    workspace.libraries?.reduce((sum, lib) => sum + (lib._count?.documents ?? 0), 0) ??
    0;

  return {
    id: workspace.id,
    name: formatWorkspaceDisplayName(workspace.name),
    rawName: workspace.name,
    ownerId: workspace.ownerId,
    kind: workspace.kind ?? "PERSONAL",
    isDefault: isDefaultWorkspace(workspace),
    description: workspace.description ?? "",
    libraryCount,
    documentCount,
    workspaceChatCount: 0,
    memberCount: computeWorkspaceMemberCount(workspace, members),
    members,
  };
}

export function mapLibrary(library: ApiLibrary): RagLibrary {
  return {
    id: library.id,
    workspaceId: library.workspaceId ?? "",
    name: library.name,
    description: library.description ?? "",
    documentCount: library._count?.documents ?? 0,
    chatCount: library._count?.threads ?? 0,
    createdById: library.createdById,
  };
}

export function mapPublicLibrary(library: ApiLibrary): RagLibrary {
  return {
    ...mapLibrary(library),
    isPublic: library.isPublic,
    updatedAt: library.updatedAt,
  };
}

export function formatUpdatedLabel(dateStr: string | undefined): string {
  if (!dateStr) return "Recently updated";
  return `Updated ${formatRelativeTime(dateStr)}`;
}

export function mapDocument(document: ApiDocument, libraryId: string): RagDocument {
  const displayTitle = documentDisplayName(document.title);
  const latestTimestamp = document.updatedAt ?? document.lastSyncedAt ?? document.createdAt;
  const isResynced =
    Boolean(document.lastSyncedAt) ||
    new Date(document.updatedAt).getTime() > new Date(document.createdAt).getTime();
  const versionLabel = document.sourceVersionId
    ? document.sourceVersionId
    : document.contentHash
      ? document.contentHash.slice(0, 12)
      : undefined;

  return {
    id: document.id,
    libraryId,
    title: displayTitle,
    type: inferDocumentType(document.mimeType, document.title),
    size: formatFileSize(document.fileSize),
    uploadedAt: formatRelativeTime(document.createdAt),
    updatedAt: formatRelativeTime(latestTimestamp),
    versionLabel,
    isResynced,
    status: STATUS_MAP[document.processingStatus] ?? "pending",
    fileUrl: document.fileUrl,
  };
}

export function mapThread(thread: ApiThread): RagChat {
  const libraryIds = thread.libraries?.map((entry) => entry.library.id) ?? [];

  const scope: RagChat["scope"] = thread.isPublicScope
    ? "public"
    : thread.workspaceId
      ? "workspace"
      : "library";

  return {
    id: thread.id,
    title: thread.title,
    scope,
    libraryId: thread.libraryId ?? undefined,
    libraryIds,
    messageCount: thread._count?.chats ?? 0,
    lastMessageAt: formatRelativeTime(thread.updatedAt),
    attachedDocCount: thread._count?.attachments ?? thread._count?.documents ?? 0,
    messages: [],
  };
}

function resolveSourcePage(metadata: ApiChatSource["metadata"]): string | undefined {
  const raw =
    metadata.pageNumber ??
    metadata.page ??
    metadata.page_number;
  const parsed = parsePageNumber(raw as string | number | null | undefined);
  return parsed != null ? String(parsed) : undefined;
}

function humanizeSourceMetadataLabel(key: string): string {
  if (key === "CELEX" || key === "ELI" || key === "EUR-Lex") {
    return key;
  }

  const stripped = key.startsWith("DC.") ? key.slice(3) : key;
  return stripped
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function isLinkableSourceAttribute(label: string, value: string): boolean {
  if (!/^https?:\/\//i.test(value)) {
    return false;
  }

  return label === "ELI" || label === "EUR-Lex" || label === "Eur Lex";
}

function buildCitationAttribute(label: string, value: string): RagCitationAttribute {
  return {
    label,
    value,
    ...(isLinkableSourceAttribute(label, value) ? { href: value } : {}),
  };
}

function mapSourceMetadataToAttributes(
  sourceMetadata: Record<string, string> | undefined,
  documentTitle: string,
): RagCitation["attributes"] {
  if (!sourceMetadata) {
    return undefined;
  }

  const attributes: NonNullable<RagCitation["attributes"]> = [];
  const seen = new Set<string>();

  for (const [key, rawValue] of Object.entries(sourceMetadata)) {
    const value = rawValue.trim();
    if (!value) {
      continue;
    }

    if (key === "title" && sourceMetadata["DC.title"]) {
      continue;
    }

    if (
      (key === "title" || key === "DC.title" || key === "DC.description")
      && value === documentTitle
    ) {
      continue;
    }

    const label = humanizeSourceMetadataLabel(key);
    const dedupeKey = `${label}:${value}`;
    if (seen.has(dedupeKey)) {
      continue;
    }

    seen.add(dedupeKey);
    attributes.push(buildCitationAttribute(label, value));
  }

  return attributes.length > 0 ? attributes : undefined;
}

function appendCellarIdAttribute(
  attributes: RagCitation["attributes"],
  cellarId: string | undefined,
  sourceMetadata: Record<string, string> | undefined,
): RagCitation["attributes"] {
  if (!cellarId?.trim()) {
    return attributes;
  }

  const hasEurlexMetadata = Boolean(
    sourceMetadata?.CELEX?.trim()
    || sourceMetadata?.ELI?.trim()
    || sourceMetadata?.["EUR-Lex"]?.trim(),
  );
  if (!hasEurlexMetadata) {
    return attributes;
  }

  const normalized = cellarId.trim();
  const next = attributes ? [...attributes] : [];
  if (next.some((attribute) => attribute.label === "Cellar ID")) {
    return next.length > 0 ? next : undefined;
  }

  next.push({ label: "Cellar ID", value: normalized });
  return next;
}

function resolveCitationDocumentTitle(metadata: ApiChatSource["metadata"]): string {
  const sourceMetadata = metadata.sourceMetadata;
  const description = sourceMetadata?.["DC.description"];
  if (description?.trim()) {
    return description.trim();
  }

  return String(metadata.title ?? "Source document");
}

export function mapSourcesToCitations(sources: ApiChatSource[] | undefined): RagCitation[] {
  if (!sources?.length) return [];

  return sources.map((source) => {
    const metadata = source.metadata ?? {};
    const documentVersionId = metadata.documentVersionId
      ? String(metadata.documentVersionId)
      : undefined;
    const contentHash = metadata.contentHash ? String(metadata.contentHash) : undefined;
    const versionLabel = metadata.versionLabel
      ? String(metadata.versionLabel)
      : contentHash
        ? contentHash.slice(0, 12)
        : undefined;
    const section = metadata.section ? String(metadata.section) : undefined;
    const documentTitle = resolveCitationDocumentTitle(metadata);
    const cellarId = metadata.cellarId ? String(metadata.cellarId) : undefined;

    return {
      id: String(source.id),
      index: source.id,
      documentId: metadata.documentId ? String(metadata.documentId) : undefined,
      attachmentId: metadata.attachmentId ? String(metadata.attachmentId) : undefined,
      documentVersionId,
      versionDownloadPath: metadata.versionDownloadPath
        ? String(metadata.versionDownloadPath)
        : undefined,
      versionLabel,
      contentHash,
      documentTitle,
      page: resolveSourcePage(metadata),
      section,
      relevance:
        source.score <= 1 ? Math.round(source.score * 100) : Math.round(source.score),
      excerpt: source.text,
      chunkLabel: metadata.chunkId ? String(metadata.chunkId) : undefined,
      attributes: appendCellarIdAttribute(
        mapSourceMetadataToAttributes(metadata.sourceMetadata, documentTitle),
        cellarId,
        metadata.sourceMetadata,
      ),
    };
  });
}

export function mapChatMessage(message: ApiChatMessage): RagMessage {
  return {
    id: message.id,
    role: message.role === "USER" ? "user" : "assistant",
    content: message.content,
    citations: mapSourcesToCitations(message.sources),
    feedback:
      message.feedback === "positive" || message.feedback === "negative"
        ? message.feedback
        : null,
  };
}

export function mapAssistantFromSendResponse(response: ApiSendMessageResponse): RagMessage {
  return {
    id: response.assistantMessage.id,
    role: "assistant",
    content: response.assistantMessage.content,
    citations: mapSourcesToCitations(response.assistantMessage.sources),
  };
}

export function createOptimisticUserMessage(content: string): RagMessage {
  return {
    id: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    role: "user",
    content,
  };
}

export function createRagErrorMessage(): RagMessage {
  return {
    id: `error-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    role: "assistant",
    content: "Sorry, there was an error processing your request. Please try again.",
  };
}

export function mapSendMessageResponse(response: ApiSendMessageResponse): RagMessage[] {
  return [mapChatMessage(response.userMessage), mapAssistantFromSendResponse(response)];
}
